/**
 * Recently Viewed Section Component
 *
 * Displays recently viewed products from Alpine store.
 *
 * Usage in Liquid:
 * <div x-data="recentlyViewedSection"
 *      data-current-product-id="{{ product.id | default: '' }}"
 *      data-limit="{{ section.settings.products_to_show | default: 4 }}"
 *      data-exclude-current="{{ section.settings.exclude_current_product | default: true }}"
 *      data-money-format="{{ shop.money_format | json | escape }}"
 *      data-product='{{ product | json | escape }}'>
 */

export default function recentlyViewedSection() {
  return {
    displayProducts: [],
    currentProductId: null,
    limit: 4,
    excludeCurrent: true,
    moneyFormat: '${{amount}}',

    init() {
      // Read configuration from data attributes
      const el = this.$el
      this.currentProductId = el.dataset.currentProductId || null
      this.limit = parseInt(el.dataset.limit) || 4
      this.excludeCurrent = el.dataset.excludeCurrent !== 'false'
      this.moneyFormat = el.dataset.moneyFormat || '${{amount}}'

      // Track current product view if on product page
      if (this.currentProductId && typeof Alpine.store('recentlyViewed') !== 'undefined') {
        this.trackCurrentProduct(el.dataset.product)
      }

      // Load and display recently viewed products
      this.loadRecentProducts()
    },

    trackCurrentProduct(productDataJson) {
      if (!productDataJson) return

      try {
        const productData = JSON.parse(productDataJson)

        // Use store to track view
        const store = Alpine.store('recentlyViewed')
        if (store && store.trackView) {
          store.trackView({
            id: productData.id,
            handle: productData.handle,
            title: productData.title,
            featured_image: productData.featured_image,
            price: productData.price,
            compare_at_price: productData.compare_at_price,
            available: productData.available
          })
        }
      } catch (e) {
        console.error('Error parsing product data:', e)
      }
    },

    loadRecentProducts() {
      const store = Alpine.store('recentlyViewed')
      if (!store) return

      // Get recent products (excluding current if needed)
      const excludeId = this.excludeCurrent ? this.currentProductId : null
      const recentProducts = store.getRecentProducts(excludeId, this.limit)

      this.displayProducts = recentProducts

      // Render products
      this.$nextTick(() => {
        this.renderProducts()
      })
    },

    renderProducts() {
      const grid = this.$refs.productsGrid
      if (!grid || this.displayProducts.length === 0) return

      // Clear existing content
      grid.innerHTML = ''

      // Render each product
      this.displayProducts.forEach(product => {
        const productCard = this.createProductCard(product)
        grid.appendChild(productCard)
      })
    },

    createProductCard(product) {
      const div = document.createElement('div')
      div.className = 'product-grid-item group relative flex flex-col bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden h-full hover:shadow-lg transition-all duration-300'
      div.setAttribute('x-data', '{ loading: false }')

      // Get product image - handle both featuredImage and featured_image
      const imageUrl = product.featuredImage || product.featured_image || ''
      const imageSrc = imageUrl ? this.getImageUrl(imageUrl, 600) : ''

      // Format prices - handle both compareAtPrice and compare_at_price
      const price = this.formatMoney(product.price)
      const compareAt = product.compareAtPrice || product.compare_at_price
      const compareAtPrice = compareAt > product.price
        ? this.formatMoney(compareAt)
        : null

      // Calculate discount percentage
      let discountPercentage = 0
      if (compareAt > product.price) {
        const discountAmount = compareAt - product.price
        discountPercentage = Math.round((discountAmount * 100) / compareAt)
      }

      // Check availability
      const isAvailable = product.available !== false

      // Check for tags (if available from localStorage)
      const tags = product.tags || []
      const isNew = tags.some(tag => tag.toLowerCase() === 'new')
      const isBestSeller = tags.some(tag => tag.toLowerCase() === 'best seller')

      // Get second image if available
      const images = product.images || []
      const secondImageUrl = images[1] || ''
      const secondImageSrc = secondImageUrl ? this.getImageUrl(secondImageUrl, 600) : ''

      // Get description if available
      const description = product.description || ''

      // Get first available variant ID
      const variants = product.variants || []
      const variantId = variants[0]?.id || ''

      // Build badges HTML
      let badgesHTML = ''
      if (isBestSeller || isNew || discountPercentage > 0) {
        badgesHTML = '<div class="absolute top-3 left-3 flex flex-col gap-2 pointer-events-none z-10">'
        if (isBestSeller) {
          badgesHTML += '<span class="text-white text-[10px] font-bold px-2 py-1 rounded uppercase tracking-wider shadow-sm" style="background-color: var(--color-accent)">Best Seller</span>'
        }
        if (isNew) {
          badgesHTML += '<span class="text-white text-[10px] font-bold px-2 py-1 rounded uppercase tracking-wider shadow-sm" style="background-color: var(--color-accent)">New</span>'
        }
        if (discountPercentage > 0) {
          badgesHTML += `<span class="text-white text-[10px] font-bold px-2 py-1 rounded uppercase tracking-wider shadow-sm" style="background-color: var(--color-accent)">${discountPercentage}% Off</span>`
        }
        badgesHTML += '</div>'
      }

      // Build second image HTML
      const secondImageHTML = secondImageSrc ? `
        <img
          src="${secondImageSrc}"
          alt="${this.escapeHtml(product.title)}"
          class="absolute inset-0 object-cover w-full h-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          loading="lazy"
          width="600"
          height="600"
        />
      ` : ''

      // Build product card HTML matching product-grid-item snippet exactly
      div.innerHTML = `
        <div class="relative w-full bg-gray-50 overflow-hidden" style="padding-top: 125%;">
          <a href="/products/${product.handle}" class="absolute inset-0 block w-full h-full">
            ${imageSrc ? `
              <img
                src="${imageSrc}"
                alt="${this.escapeHtml(product.title)}"
                class="absolute inset-0 object-cover w-full h-full transition-opacity duration-500"
                loading="lazy"
                width="600"
                height="600"
              />
              ${secondImageHTML}
            ` : `
              <div class="absolute inset-0 flex items-center justify-center text-gray-400 text-sm">
                No image
              </div>
            `}
          </a>

          ${badgesHTML}

          <button
            type="button"
            @click.prevent="$dispatch('open-quick-view', { productHandle: '${product.handle}' })"
            class="absolute bottom-3 right-3 bg-white/90 backdrop-blur text-gray-900 p-2 rounded-full shadow-sm opacity-0 group-hover:opacity-100 transition-all focus:opacity-100 z-10"
            aria-label="Quick view"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
            </svg>
          </button>
        </div>

        <div class="flex flex-col p-4 gap-2 grow">
          <h3 class="font-bold text-gray-900 leading-snug line-clamp-2 min-h-10">
            <a href="/products/${product.handle}" class="hover:text-gray-600 transition-colors">
              ${this.escapeHtml(product.title)}
            </a>
          </h3>

          <div class="text-xs text-gray-500 line-clamp-2 min-h-[2em]">
            ${description}
          </div>

          <div class="flex items-center gap-2 mt-2">
            <span class="font-bold text-lg" style="color: var(--color-accent)">${price}</span>
            ${compareAtPrice ? `
              <span class="text-xs text-gray-400 line-through">${compareAtPrice}</span>
              <span class="text-[10px] font-bold text-white px-1.5 py-0.5 rounded shadow-sm bg-orange-500">
                ${discountPercentage}% OFF
              </span>
            ` : ''}
          </div>
        </div>

        <div class="p-4 pt-0 mt-auto">
          <ajax-cart-product-form>
            <form
              action="/cart/add"
              method="post"
              enctype="multipart/form-data"
              @submit="loading = true; setTimeout(() => loading = false, 2000)"
              @liquid-ajax-cart:request-end.window="loading = false"
              class="w-full"
            >
              <input type="hidden" name="id" value="${variantId}">
              <button
                type="submit"
                name="add"
                class="relative w-full py-2.5 px-4 rounded-lg text-white text-xs font-bold uppercase tracking-wide transition-all duration-300 flex items-center justify-center gap-2 hover:brightness-110 disabled:opacity-70 disabled:cursor-not-allowed min-h-[40px]"
                style="background-color: var(--color-accent);"
                :disabled="${!isAvailable} || loading"
              >
                <span
                  :class="loading ? 'opacity-0' : 'opacity-100'"
                  class="flex items-center gap-2 transition-opacity duration-200"
                >
                  ${isAvailable ? 'Add to Cart' : 'Sold Out'}
                </span>

                <div
                  x-show="loading"
                  class="absolute inset-0 flex items-center justify-center"
                  x-transition
                >
                  <svg
                    class="animate-spin h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
              </button>
            </form>
          </ajax-cart-product-form>
        </div>
      `

      return div
    },

    getImageUrl(src, width = 600) {
      if (!src) return ''
      const baseUrl = src.split('?')[0]
      return `${baseUrl}?width=${width}`
    },

    formatMoney(cents) {
      if (window.vast && window.vast.helpers && window.vast.helpers.formatMoney) {
        return window.vast.helpers.formatMoney(cents, this.moneyFormat)
      }
      return `$${(cents / 100).toFixed(2)}`
    },

    escapeHtml(text) {
      const div = document.createElement('div')
      div.textContent = text
      return div.innerHTML
    }
  }
}
